import TextField from 'core-system/TextField'
import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import SegmentService from 'redux/config/services/SegmentService'
import UserService from 'redux/user/userService'
import styled from 'styled-components'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import Text from 'core-system/Text'

const StatusText = styled.p<{ error?: boolean }>`
  margin-top: 1rem;
  ${(props) => props.theme.typography.caption};
  font-size: ${(props) => (props.error ? '0.75rem' : '1rem')};
  color: ${(props) =>
    props.error
      ? props.theme.palette.error.red500
      : props.theme.palette.primary.pink800};
`

const InfoText = styled.div`
  color: ${(props) => props.theme.palette.text.primary};
  ${(props) => props.theme.typography.body1};
  margin-top: 2rem;
`

const StyledLink = styled(Link)`
  margin: 0;
  color: ${(props) => props.theme.palette.primary.pink700};
`

const ForgotPasswordForm = React.memo(
  ({ setPageState }: { setPageState: (pageState: string) => void }) => {
    const [email, setEmail] = useState<string>('')
    const [showSuccessMsg, setShowSuccessMsg] = useState<boolean>(false)
    const [errorMsg, setShowErrorMsg] = useState<string>('')

    const onSubmit = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()

        if (email.trim() !== '') {
          UserService.resetPassword(email.toLowerCase())
            .then(() => {
              setEmail('')
              setShowSuccessMsg(true)
            })
            .catch((err) => {
              setShowErrorMsg(
                err.response.data.email
                  ? err.response.data.email[0]
                  : 'Oops, something went wrong. Please try again or contact support@movewithfleet.com'
              )
            })

          SegmentService.track('email-resetLink-action', { email: email })
        }
      },
      [email, setEmail, setShowSuccessMsg, setShowErrorMsg]
    )

    const onChange = useCallback(
      (val: string) => {
        setEmail(val)
        if (errorMsg !== '') {
          setShowErrorMsg('')
        }
      },
      [errorMsg]
    )
    return (
      <div>
        <Text variant='h1' marginBottom='1.5rem'>
          Forgot Your Password?
        </Text>
        <Text variant='action2' marginBottom='2rem'>
          No problem. We will email you a link you can use to reset your
          password.
        </Text>
        <form>
          <TextField
            type='email'
            label='Email*'
            defaultValue={email}
            placeholder='email'
            onChange={(e) => onChange(e.currentTarget.value)}
            invalid={errorMsg !== ''}
            helpText={errorMsg}
          />
          <PrimaryButton onClick={onSubmit} style={{ marginTop: '2rem' }}>
            Email Password Reset Link
          </PrimaryButton>
        </form>
        {showSuccessMsg && (
          <StatusText>Please check your email for next steps.</StatusText>
        )}
        <InfoText>
          Have an account?{' '}
          <StyledLink onClick={() => setPageState('login')}>Login</StyledLink>
        </InfoText>
      </div>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ForgotPasswordForm.displayName = 'ForgotPasswordForm'
}

export default ForgotPasswordForm
